import * as debug from './debug';

/**
 * Fetches the Telegram user's name and phone from the backend.
 *
 * Telegram's initData contains: id, first_name, last_name, username,
 * language_code, is_premium, allows_write_to_pm — no phone number.
 * The backend validates the initData signature and returns name fields
 * from it; phone is resolved separately (e.g. from a database lookup
 * by Telegram user ID) and may be absent.
 *
 * Falls back to { name: '', phone: '+380' } when:
 *   - the app is opened outside Telegram (no initData)
 *   - the backend request fails for any reason
 *   - the backend does not return a phone for this user
 *
 * @returns {Promise<{ name: string, phone: string }>}
 */
export async function getTelegramUser() {
    const tg        = window.Telegram;
    const webApp    = tg?.WebApp;
    const initData  = webApp?.initData;
    const initDataUnsafe = webApp?.initDataUnsafe;
    const platform  = webApp?.platform;
    const version   = webApp?.version;

    debug.log('getTelegramUser diagnostics:');
    debug.log('  window.Telegram present =', !!tg);
    debug.log('  window.Telegram.WebApp present =', !!webApp);
    debug.log('  platform =', platform ?? '(undefined)');
    debug.log('  version =', version ?? '(undefined)');
    debug.log('  initData type =', typeof initData);
    debug.log('  initData length =', initData?.length ?? 'n/a');
    debug.log('  initData value =', initData ? initData.slice(0, 80) + '…' : JSON.stringify(initData));
    debug.log('  initDataUnsafe.user =', initDataUnsafe?.user ?? '(none)');

    if (!initData) {
        debug.warn('getTelegramUser: no initData — not a Telegram context, skipping contact fetch');
        return { name: '', phone: '+380', contactNotFound: false };
    }

    const botApi = window.__ENV__?.TELEGRAM_BOT_API;
    debug.log('getTelegramUser: TELEGRAM_BOT_API =', botApi || '(empty — nginx proxy must be configured separately)');

    try {
        const url = '/tgbot/api/contact';
        debug.log('getTelegramUser: POST', url);
        const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ init_data: initData }),
        });
        const rawText = await res.text();
        debug.log('getTelegramUser: HTTP status =', res.status);
        debug.log('getTelegramUser: response body =', rawText.slice(0, 200));

        if (res.status === 404) {
            debug.warn('getTelegramUser: 404 — user has not shared contact via bot yet');
            return { name: '', phone: '+380', contactNotFound: true };
        }

        if (!res.ok) {
            debug.error('getTelegramUser: non-OK response', res.status, rawText.slice(0, 200));
            return { name: '', phone: '+380', contactNotFound: false };
        }
        const contact = JSON.parse(rawText);
        const nameParts = [contact.first_name, contact.last_name].filter(Boolean);
        const name = nameParts.join(' ');
        const rawPhone = contact.phone ?? '';
        const phone = rawPhone
            ? (rawPhone.startsWith('+') ? rawPhone : `+${rawPhone}`)
            : '+380';
        debug.log('getTelegramUser: resolved name =', name, '| phone =', phone);
        return { name, phone, contactNotFound: false };
    } catch (err) {
        debug.error('getTelegramUser: fetch threw —', err);
        return { name: '', phone: '+380', contactNotFound: false };
    }
}
